import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';

import { currencyCode } from 'Helpers/Geo/data';
import SpecialFormattedNumber from 'Containers/Utility/Price/SpecialFormattedNumber';

export default function Terms({ voucher, isDelivery }) {
  const { formatList } = useIntl();

  if (!voucher) {
    return false;
  }

  const {
    min_spend: minSpend,
    max_spend: maxSpend,
    required_products: requiredProducts,
    excluded_products: excludedProducts,
    required_facets: requiredFacets,
    excluded_facets: excludedFacets,
    dynamic_valid_until: dynamicValidUntil,
    valid_until: validUntil,
    game,
    bundle,
    dlc,
    star_deal: starDeal,
    preorder,
    pick_and_mix: pickAndMix,
    save_more: saveMore,
    single_use: singleUse,
    newsletter,
    full_price_only: fullPriceOnly,
    assistant_exclusive: assistantExclusive,
  } = voucher;

  let minSpendBool = false;
  if (voucher && minSpend && minSpend[`${currencyCode}`] > 0) {
    minSpendBool = true;
  }

  let maxSpendBool = false;
  if (voucher && maxSpend && maxSpend[`${currencyCode}`] > 0) {
    maxSpendBool = true;
  }

  const hasExclusions = (excludedProducts && !!excludedProducts.length) ||
    game === false ||
    bundle === false ||
    dlc === false ||
    starDeal === false ||
    preorder === false ||
    pickAndMix === false ||
    saveMore === false ||
    singleUse ||
    newsletter ||
    fullPriceOnly ||
    assistantExclusive ||
    (excludedFacets && !!excludedFacets.length);

  let isDynamicValidUntil = false;
  if (dynamicValidUntil) isDynamicValidUntil = true;

  return (
    <div>
      {minSpendBool &&
        <span>
          <FormattedMessage id="voucher-box-terms.min-spend" defaultMessage="Minimum spend" />
          {/* eslint-disable react/style-prop-object */}
          &nbsp;
          <SpecialFormattedNumber
            value={minSpend[`${currencyCode}`] / 100}
            style="currency"
          />
          .&nbsp;
        </span>
      }
      {maxSpendBool &&
        <span>
          <FormattedMessage id="voucher-box-terms.max-spend" defaultMessage="Maximum spend" />
          {/* eslint-disable react/style-prop-object */}
          &nbsp;
          <SpecialFormattedNumber
            value={maxSpend[`${currencyCode}`] / 100}
            style="currency"
          />
          .&nbsp;
        </span>
      }
      {!isDelivery &&
        <span>
          {isDynamicValidUntil &&
            <FormattedMessage
              id="voucher-box-terms.voucher-expires-dynamic"
              defaultMessage="Valid for {days} days after date of purchase."
              values={{
                days: dynamicValidUntil,
              }}
            />
          }
          {!isDynamicValidUntil &&
            <FormattedMessage
              id="voucher-box-terms.voucher-expires"
              defaultMessage="Valid until {date}."
              values={{
                date: <FormattedDate value={validUntil} year="numeric" month="long" day="2-digit" />,
              }}
            />
          }
        </span>
      }
      {requiredProducts && !!requiredProducts.length &&
        <div className="mt-2">
          <b>
            <FormattedMessage id="voucher-box-terms.required" defaultMessage="Valid only on these products:" />
          </b>
          <ul>
            {orderBy(uniqBy(requiredProducts, 'slug'), ['slug']).map(product =>
              <li key={product.slug}><Link to={`${product.type}/${product.slug}`}>{product.name}</Link></li>,
            )}
          </ul>
        </div>
      }
      {requiredFacets && !!requiredFacets.length &&
        <div className="mt-2">
          <FormattedMessage
            id="voucher-box-terms.required-facets"
            defaultMessage="Valid only on {list} products"
            values={{
              list: formatList(requiredFacets.map(f => <b>{f}</b>)),
            }}
          />
        </div>
      }
      {hasExclusions &&
        <div className="mt-2">
          <b>
            <FormattedMessage id="voucher-box-terms.exclusions" defaultMessage="Exclusions apply:" />
          </b>
          <ul>
            {game === false &&
              <li>
                <FormattedMessage id="voucher-box-terms.voucher-not-valid" defaultMessage="Not valid on Games" />
              </li>
            }
            {bundle === false &&
              <li>
                <FormattedMessage id="voucher-box-terms.voucher-not-valid-bundle" defaultMessage="Not valid on Bundles" />
              </li>
            }
            {dlc === false &&
              <li>
                <FormattedMessage id="voucher-box-terms.voucher-not-valid-dlc" defaultMessage="Not valid on DLC" />
              </li>
            }
            {starDeal === false &&
              <li>
                <FormattedMessage id="voucher-box-terms.voucher-not-valid-star_deal" defaultMessage="Not valid on Star Deals" />
              </li>
            }
            {preorder === false &&
              <li>
                <FormattedMessage id="voucher-box-terms.voucher-not-valid-preorder" defaultMessage="Not valid on Preorders and New Releases" />
              </li>
            }
            {pickAndMix === false &&
              <li>
                <FormattedMessage id="voucher-box-terms.voucher-not-valid-pick_and_mix" defaultMessage="Not valid on Pick & Mixes" />
              </li>
            }
            {saveMore === false &&
              <li>
                <FormattedMessage id="voucher-box-terms.voucher-not-valid-save_more" defaultMessage="Not valid on Buy More Save Mores" />
              </li>
            }
            {singleUse &&
              <li>
                <FormattedMessage id="voucher-box-terms.voucher-transaction-only" defaultMessage="You can only use this coupon on one transaction" />
              </li>
            }
            {newsletter &&
              <li>
                <FormattedMessage id="voucher-box-terms.must-subscribe" defaultMessage="You must be subscribed to the Fanatical newsletter" />
              </li>
            }
            {fullPriceOnly &&
              <li>
                <FormattedMessage id="voucher-box-terms.full_price_only" defaultMessage="Not valid on discounted products" />
              </li>
            }
            {assistantExclusive &&
              <li>
                <FormattedMessage
                  id="voucher-box-terms.must-have"
                  defaultMessage="You must have {fanAssistLink} installed"
                  values={{
                    fanAssistLink: <Link to={'/browser-extension-'}><FormattedMessage id="voucher-box-terms.fan-assist" defaultMessage="Fanatical Browser Extension" /></Link>,
                  }}
                />
              </li>
            }
            {excludedFacets && !!excludedFacets.length &&
              <li>
                <FormattedMessage
                  id="voucher-box-terms.excluded-facets"
                  defaultMessage="Not valid on {list} products"
                  values={{
                    list: formatList(excludedFacets.map(f => <b>{f}</b>), { type: 'disjunction' }),
                  }}
                />
              </li>
            }
            {excludedProducts && !!excludedProducts.length &&
              <li>
                <FormattedMessage id="voucher-box-terms.product-exlusions" defaultMessage="Some individual product exclusions apply"/>
              </li>
            }
          </ul>
        </div>
      }
    </div>
  );
}

Terms.propTypes = {
  voucher: PropTypes.shape({}).isRequired,
  isDelivery: PropTypes.bool,
};

Terms.defaultProps = {
  isDelivery: false,
};
